import React, {
  useEffect,
  useState,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Box,
  Container,
  makeStyles
} from '@material-ui/core';

import {
  clearTagsData,
  deleteTag,
  fetchTags,
  setFilters,
} from 'src/store/actions/tags';
import tagsSelectors from 'src/store/selectors/tags';

import AlertDialog from 'src/components/AlertDialog';
import Page from 'src/components/Page';
import Results from '../components/Results';
import Toolbar from './Toolbar';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  }
}));

const TagsListView = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const deleteAlertRef = useRef();

  const [isInited, setIsInited] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const search = searchParams.get('search') || null;
  const currentPage = parseInt(searchParams.get('page') || 1, 10);
  const rowsPerPage = parseInt(searchParams.get('limit') || 10, 10);

  const isFetching = useSelector((state) => tagsSelectors.getTagsIsFetching(state));
  const filters = useSelector((state) => tagsSelectors.getFilters(state));

  const fetchData = async () => {
    try {
      await dispatch(fetchTags({
        search,
        page: currentPage,
        limit: rowsPerPage,
        ...filters,
      }));
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Wystąpił problem podczas pobierania listy tagów', { variant: 'error' });
    }
  };

  const setFiltersFromSearchParams = () => {
    // eslint-disable-next-line no-unused-vars
    const { page, limit, ...filtersArray } = Object.fromEntries(searchParams);

    dispatch(setFilters(filtersArray));
  };

  useEffect(() => {
    setIsInited(true);
    return () => {
      dispatch(clearTagsData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInited) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    setFiltersFromSearchParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleChangePage = (event, value) => {
    setSearchParams({
      page: value + 1,
      limit: rowsPerPage,
      ...filters
    });
  };

  const handleLimitChange = (event) => {
    const { value } = event.target;
    setSearchParams({
      page: currentPage,
      limit: value,
      ...filters
    });
  };

  const handleSearch = (values) => {
    setSearchParams({
      page: currentPage,
      limit: rowsPerPage,
      ...values
    });
  };

  const openDeleteModal = (id) => {
    deleteAlertRef.current.open();
    setSelectedId(id);
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteTag(selectedId));
      deleteAlertRef.current.close();
      setSelectedId(null);
      enqueueSnackbar('Tag został usunięty', { variant: 'success' });

      await fetchData();

      return true;
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Wystąpił problem podczas usuwania tagu', { variant: 'error' });

      return error;
    }
  };

  return (
    <Page
      className={classes.root}
      title="Tagi"
    >
      <Container maxWidth={false}>
        <Toolbar handleSearch={handleSearch} />
        <Box mt={3}>
          <Results
            handleChangePage={handleChangePage}
            handleLimitChange={handleLimitChange}
            openDeleteModal={openDeleteModal}
          />
        </Box>
      </Container>
      <AlertDialog
        title="Usuwanie tagu"
        message="Czy na pewno chcesz usunąć ten tag?"
        ref={deleteAlertRef}
        actions={[
          {
            label: 'Anuluj',
          },
          {
            autoFocus: true,
            disabled: isFetching,
            label: 'Usuń',
            loading: isFetching,
            variant: 'contained',
            onClick: handleDelete,
          },
        ]}
      />
    </Page>
  );
};

export default TagsListView;
